import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Slider from "react-slick";
import Image from "../../components/parts/Image";
import Tag from "../../components/parts/Tag";
import Text from "../../components/parts/Text";
import Divider from "../../components/parts/Divider";
import HeaderOne from "../../components/parts/HeaderOne";
import Table from "../../components/parts/Table";
import HeaderTwo from "../../components/parts/HeaderTwo";
import { api } from "../../../api";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import parse from "html-react-parser";
import * as Sentry from "@sentry/react";
import {
  GetOwnerDraftShop200Response,
  GetOwnerDraftShops200ResponseInnerApprovalStatusEnum,
} from "../../../api/v1";
import OwnerLayout from "../../components/layouts/OwnerLayout";

const HeaderOneStyle = styled(HeaderOne)`
  margin-bottom: 20px;
  text-align: center;

  span {
    white-space: nowrap;
  }
`;

const DraftShopStyle = styled.div`
  background: white;
  border-radius: 25px;
  padding: 20px 30px;
  margin: 30px;

  @media (max-width: 767px) {
    padding: 20px 10px;
    margin: 30px 10px;
  }
`;

const DraftShopImagesStyle = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 40px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    img {
      margin-right: 0;
      width: calc(50% - 5px);
    }
  }
`;

const SlickDraftShopImagesStyle = styled.div`
  margin-bottom: 40px;
`;

const DraftShopCardsSliderStyle = styled(Slider)`
  .slick-list {
    margin-right: -9px;
    padding-bottom: 20px;
  }

  .slick-slide > div {
    padding: 0 10px 10px 0;
  }
`;

const GoogleMapStyle = styled.div`
  margin-bottom: 30px;
  iframe {
    width: 100%;
    height: 300px;
  }
`;

const OwnerDraftShop: React.FC = () => {
  const settings = {
    adaptiveHeight: true,
    arrows: false,
    autoplay: false,
    dots: true,
    infinite: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1000,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          rows: 1,
        },
      },
    ],
  };
  const dayOfWeeks = {
    sunday: "日曜日",
    monday: "月曜日",
    tuesday: "火曜日",
    wednesday: "水曜日",
    thursday: "木曜日",
    friday: "金曜日",
    saturday: "土曜日",
    holiday: "祝日",
  };
  const timeframe = {
    morning: "モーニング",
    lunch: "ランチ",
    dinner: "ディナー",
  };
  const [shop, setShop] = useState<GetOwnerDraftShop200Response>();

  useEffect(() => {
    const id = location.pathname.split("/").pop();
    api
      .getOwnerDraftShop(Number(id))
      .then((response) => {
        setShop(response.data);
      })
      .catch((error) => {
        if (error.response?.status === 401) location.href = "/owner/shops";
        Sentry.captureException(error);
      });
  }, []);

  if (!shop) return;

  const PageTitle = ({
    approvalStatus,
  }: {
    approvalStatus: GetOwnerDraftShops200ResponseInnerApprovalStatusEnum;
  }) => {
    switch (approvalStatus) {
      case GetOwnerDraftShops200ResponseInnerApprovalStatusEnum.Approved:
        return <HeaderOneStyle>申請の確認（承認済み）</HeaderOneStyle>;
      case GetOwnerDraftShops200ResponseInnerApprovalStatusEnum.Rejected:
        return <HeaderOneStyle>申請の確認（却下）</HeaderOneStyle>;
      default:
        return <HeaderOneStyle>申請の確認（未承認）</HeaderOneStyle>;
    }
  };

  return (
    <OwnerLayout>
      <DraftShopStyle>
        <PageTitle approvalStatus={shop.approvalStatus} />
        <Text size="12px" mb="5px">
          申請日時：{new Date(shop.createdAt).toLocaleString()}
        </Text>
        <HeaderOne>{shop.name}</HeaderOne>
        <Text size="12px">{shop.nameKana}</Text>
        {shop.nearestStation && <Tag mr="5px">{shop.nearestStation}</Tag>}
        {shop.features
          .map((feature) => feature.name)
          .includes("モーニング") && <Tag mr="5px">モーニング</Tag>}
        {shop.features.map((feature) => feature.name).includes("ランチ") && (
          <Tag mr="5px">ランチ</Tag>
        )}
        {shop.features.map((feature) => feature.name).includes("ディナー") && (
          <Tag>ディナー</Tag>
        )}
        <Divider mt="20px" mb="20px" />
        {shop.images.length <= 4 ? (
          <DraftShopImagesStyle>
            {shop.images.map((shop, index) => {
              return <Image key={index} src={shop.url} alt="店舗画像" />;
            })}
          </DraftShopImagesStyle>
        ) : (
          <SlickDraftShopImagesStyle>
            <DraftShopCardsSliderStyle {...settings}>
              {shop.images.map((shop, index) => {
                return (
                  <Image
                    key={index}
                    src={shop.url}
                    width="100%"
                    alt="店舗画像"
                  />
                );
              })}
            </DraftShopCardsSliderStyle>
          </SlickDraftShopImagesStyle>
        )}
        <Text size="16px" mb="30px">
          {shop.description}
        </Text>
        {shop.googleMapIframe && (
          <section>
            <HeaderTwo mb="8px">Googleマップ</HeaderTwo>
            <GoogleMapStyle>{parse(shop.googleMapIframe)}</GoogleMapStyle>
          </section>
        )}
        <section>
          <HeaderTwo mb="5px">店舗基本情報</HeaderTwo>
          <Table
            contents={[
              {
                head: "営業時間",
                data: shop.draftOpeningHours.map((openingHour, index) => {
                  return (
                    <>
                      <p key={index}>
                        {`${dayOfWeeks[openingHour.dayOfWeek]} ${
                          openingHour.openingTime
                        } 〜 ${openingHour.closingTime}`}
                      </p>
                      {shop.draftOpeningHours.length === index + 1 &&
                        "営業時間や定休日は変更になっている場合がございますので、ご来店前に店舗にご確認ください。"}
                    </>
                  );
                }),
              },
              { head: "電話番号", data: shop.tel },
              { head: "所在地", data: shop.address },
              { head: "最寄駅", data: shop.nearestStation },
              {
                head: "お支払い方法",
                data: shop.paymentMethods
                  .map((paymentMethod) => paymentMethod.name)
                  .join("、"),
              },
              {
                head: "予算",
                data: shop.draftBudgets.map((draftBudget) => {
                  return (
                    <p key={draftBudget.timeframe}>
                      {timeframe[draftBudget.timeframe]}：{draftBudget.lower}円
                      〜 {draftBudget.upper}円
                    </p>
                  );
                }),
              },
              {
                head: "座席数",
                data: shop.numberOfSeats && shop.numberOfSeats + "席",
              },
              {
                head: "ホームページ",
                data: (
                  <a href={shop.homepage} target="_blank">
                    {shop.homepage}
                  </a>
                ),
              },
              {
                head: "Googleマップ共有リンク",
                data: (
                  <a href={shop.googleMapUrl} target="_blank">
                    {shop.googleMapUrl}
                  </a>
                ),
              },
              {
                head: "特徴",
                data: shop.features.map((feature) => feature.name).join("、"),
              },
              {
                head: "備考",
                data: shop.note && parse(shop.note.replace(/\n/g, "<br />")),
              },
            ]}
          />
        </section>
      </DraftShopStyle>
    </OwnerLayout>
  );
};
export default OwnerDraftShop;
